import { useEffect, useState } from "react";
import { isEmptyGameParams, useGameParamsContext } from "../stores/gameParams";
import { bridge } from "./bridge";

// JWT segments are base64url, which atob does not accept.
const decodePayload = (jwt: string): Record<string, unknown> | undefined => {
    try {
        const jwtParts = jwt.split(".");

        if (jwtParts.length !== 3) {
            return undefined;
        }

        const base64 = (jwtParts[1] ?? "")
            .replace(/-/g, "+")
            .replace(/_/g, "/");
        return JSON.parse(atob(base64));
    } catch {
        return undefined;
    }
};

export const getAvatarId = (jwt: string): number | undefined => {
    const payload = decodePayload(jwt);
    return payload && "avatarId" in payload
        ? (payload.avatarId as number)
        : undefined;
};

export const getExpirationDate = (jwt: string): Date | undefined => {
    const payload = decodePayload(jwt);
    return payload && typeof payload.exp === "number"
        ? new Date(payload.exp * 1000)
        : undefined;
};

export function useJwt() {
    const [jwt, setJWT] = useState<string | null>(null);
    const { gameParams } = useGameParamsContext();

    // TODO: Deprecate gameParams and use getJWT instead, when available

    useEffect(() => {
        (async () => {
            const jwt = !isEmptyGameParams(gameParams)
                ? gameParams.jwt
                : undefined;

            if (!jwt) {
                console.error("No JWT available from gameParams");

                await bridge().then((b) => b.refreshJwt());
                return;
            }

            setJWT(jwt);
            console.log("Set JWT", jwt);

            const jwtExpiration = getExpirationDate(jwt);

            if (jwtExpiration && jwtExpiration < new Date()) {
                console.error("JWT is expired");
                await bridge().then((b) => b.refreshJwt());
                return;
            }
        })();
    }, [gameParams]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        function handleJWTUpdate(e: any) {
            const { jwt } = e.detail;
            if (jwt) {
                setJWT(jwt);
                console.log("Set JWT", jwt);
            }
        }
        window.addEventListener("jwtUpdated", handleJWTUpdate);

        return () => {
            window.removeEventListener("jwtUpdated", handleJWTUpdate);
        };
    }, []);

    return jwt;
}
